import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useI18n } from "../lib/i18n";

const CGVPage = () => {
  const { locale } = useI18n();
  const [searchParams] = useSearchParams();
  const backLink = searchParams.get("from") === "patient" ? "/bienvenue" : "/";

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to={backLink} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="w-4 h-4" />
          {locale === "fr" ? "Retour à l'accueil" : "Back to home"}
        </Link>

        <h1 className="font-heading text-4xl font-bold mb-8">
          {locale === "fr" ? "Conditions Générales de Vente" : "Terms of Sale"}
        </h1>

        <div className="prose dark:prose-invert max-w-none space-y-6 text-muted-foreground">
          <p className="text-sm">{locale === "fr" ? "Dernière mise à jour : Mars 2026" : "Last updated: March 2026"}</p>

          <section>
            <h2 className="text-xl font-semibold text-foreground">{locale === "fr" ? "1. Objet" : "1. Purpose"}</h2>
            <p>{locale === "fr"
              ? "Les présentes Conditions Générales de Vente (CGV) régissent les relations contractuelles entre l'éditeur de la plateforme Kintsugi TCC et les thérapeutes souscrivant à un abonnement payant."
              : "These Terms of Sale govern the contractual relationship between the Kintsugi TCC platform publisher and therapists subscribing to a paid plan."}</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">{locale === "fr" ? "2. Offres d'abonnement" : "2. Subscription Plans"}</h2>
            <p>{locale === "fr" ? "Deux formules d'abonnement sont proposées :" : "Two subscription plans are available:"}</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>{locale === "fr" ? "Formule Mensuelle" : "Monthly Plan"}</strong>{locale === "fr"
                  ? " : 49€ TTC par mois, avec un engagement minimum de 12 mois. Au-delà de la période d'engagement, l'abonnement est renouvelé automatiquement chaque mois."
                  : ": €49 incl. tax per month, with a minimum 12-month commitment. After the commitment period, the subscription renews automatically each month."}
              </li>
              <li>
                <strong>{locale === "fr" ? "Formule Annuelle" : "Annual Plan"}</strong>{locale === "fr"
                  ? " : 44,92€ TTC par mois (soit 539€ TTC par an, équivalent à 1 mois offert). Paiement mensuel. Engagement de 12 mois, renouvelé automatiquement."
                  : ": €44.92 incl. tax per month (€539 incl. tax per year, equivalent to 1 month free). Monthly billing. 12-month commitment, auto-renewed."}
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">{locale === "fr" ? "3. Période d'essai" : "3. Free Trial"}</h2>
            <p>{locale === "fr"
              ? "Chaque nouvel abonnement bénéficie d'une période d'essai gratuite de 15 jours. Pendant cette période, vous avez accès à l'ensemble des fonctionnalités. Aucun prélèvement n'est effectué pendant l'essai. Si vous n'annulez pas avant la fin de l'essai, l'abonnement sera automatiquement activé et le premier paiement prélevé."
              : "Each new subscription includes a 15-day free trial. During this period, you have access to all features. No payment is charged during the trial. If you don't cancel before the trial ends, the subscription will be automatically activated and the first payment charged."}</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">{locale === "fr" ? "4. Paiement" : "4. Payment"}</h2>
            <p>{locale === "fr"
              ? "Les paiements sont effectués par carte bancaire via Stripe, un prestataire de paiement sécurisé conforme PCI-DSS. Le prélèvement est mensuel et automatique. Kintsugi TCC ne stocke aucune donnée bancaire."
              : "Payments are made by credit card via Stripe, a PCI-DSS compliant secure payment provider. Billing is monthly and automatic. Kintsugi TCC does not store any banking data."}</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">{locale === "fr" ? "5. Renouvellement et résiliation" : "5. Renewal and Cancellation"}</h2>
            <p>{locale === "fr"
              ? "L'abonnement est renouvelé automatiquement à chaque échéance. Vous pouvez annuler votre abonnement à tout moment depuis votre espace client. L'annulation prend effet à la fin de la période en cours. Aucun remboursement n'est effectué pour la période en cours. Pendant la période d'engagement de 12 mois, l'annulation anticipée n'est pas possible sauf dans les cas prévus par la loi."
              : "The subscription is automatically renewed at each billing period. You can cancel your subscription at any time from your account settings. Cancellation takes effect at the end of the current period. No refund is issued for the current period. During the 12-month commitment period, early cancellation is not possible except as provided by law."}</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">{locale === "fr" ? "6. Délai de grâce" : "6. Grace Period"}</h2>
            <p>{locale === "fr"
              ? "En cas d'échec de paiement, un délai de grâce de 24 heures est accordé pour régulariser la situation. Passé ce délai, l'accès aux fonctionnalités payantes sera suspendu jusqu'au paiement du montant dû."
              : "In case of payment failure, a 24-hour grace period is granted to resolve the situation. After this period, access to paid features will be suspended until the outstanding amount is paid."}</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">{locale === "fr" ? "7. Droit de rétractation" : "7. Right of Withdrawal"}</h2>
            <p>{locale === "fr"
              ? "Conformément à l'article L221-28 du Code de la consommation, le droit de rétractation ne s'applique pas aux services pleinement exécutés avant la fin du délai de rétractation et dont l'exécution a commencé après accord préalable exprès du consommateur. En acceptant ces CGV et en commençant à utiliser le service, vous renoncez expressément à votre droit de rétractation."
              : "In accordance with Article L221-28 of the French Consumer Code, the right of withdrawal does not apply to services fully performed before the end of the withdrawal period, the performance of which began with the consumer's prior express agreement. By accepting these terms and starting to use the service, you expressly waive your right of withdrawal."}</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">{locale === "fr" ? "8. Modification des tarifs" : "8. Price Changes"}</h2>
            <p>{locale === "fr"
              ? "L'éditeur se réserve le droit de modifier les tarifs. Toute modification sera communiquée aux abonnés par email au moins 30 jours avant son entrée en vigueur. Les abonnés pourront résilier leur abonnement avant l'application des nouveaux tarifs."
              : "The publisher reserves the right to modify prices. Any change will be communicated to subscribers by email at least 30 days before it takes effect. Subscribers may cancel their subscription before the new prices apply."}</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">{locale === "fr" ? "9. Loi applicable" : "9. Applicable Law"}</h2>
            <p>{locale === "fr"
              ? "Les présentes CGV sont régies par le droit français. En cas de litige, les tribunaux compétents de Paris seront compétents."
              : "These Terms of Sale are governed by French law. In case of dispute, the competent courts of Paris shall have jurisdiction."}</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground">{locale === "fr" ? "10. Contact" : "10. Contact"}</h2>
            <p>{locale === "fr"
              ? "Pour toute question relative à votre abonnement, contactez-nous à : contact@kintsugi-tcc.fr"
              : "For any questions about your subscription, contact us at: contact@kintsugi-tcc.fr"}</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CGVPage;
